import Keycloak from "keycloak-js";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { env } from "@/app/env";
import { queryClient } from "@/app/query-client";
import { AuthContext, type AuthContextValue, type AuthStatus } from "@/auth/auth-context";
import {
  licensingRoles,
  TokenSession,
  type AuthTokenSnapshot,
  type LicensingRole,
  type TokenSessionSource
} from "@/auth/token-session";

const baseUrl = import.meta.env.BASE_URL;
const silentCheckSsoPath = "silent-check-sso.html";

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const keycloakRef = useRef<Keycloak | null>(null);
  const tokenSessionRef = useRef(new TokenSession());
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [userName, setUserName] = useState("");
  const [roles, setRoles] = useState<ReadonlySet<LicensingRole>>(new Set());

  useEffect(() => {
    const tokenSession = tokenSessionRef.current;
    if (env.e2eAuthToken) {
      const roleFixture = window.localStorage.getItem("licensing-e2e-auth-roles");
      const e2eRoles = parseSupportedRoles((roleFixture ?? env.e2eAuthRoles).split(","));
      tokenSession.establish({
        updateToken: async () => false,
        readToken: () => env.e2eAuthToken,
        readRoles: () => e2eRoles
      });
      setRoles(e2eRoles);
      setUserName(env.e2eAuthUser);
      setStatus(e2eRoles.size > 0 ? "authenticated" : "forbidden");
      return () => tokenSession.clear();
    }

    let mounted = true;
    const keycloak = new Keycloak({
      url: env.keycloakUrl,
      realm: env.keycloakRealm,
      clientId: env.keycloakClientId
    });
    keycloakRef.current = keycloak;

    void keycloak
      .init({
        onLoad: "check-sso",
        pkceMethod: "S256",
        checkLoginIframe: false,
        silentCheckSsoFallback: false,
        silentCheckSsoRedirectUri: new URL(silentCheckSsoPath, `${window.location.origin}${baseUrl}`).toString()
      })
      .then((authenticated) => {
        if (!mounted) {
          return;
        }
        if (!authenticated) {
          setStatus("anonymous");
          return;
        }
        const source = keycloakTokenSource(keycloak);
        const snapshot = tokenSession.establish(source);
        setRoles(snapshot.roles);
        setUserName(readUserName(keycloak));
        setStatus(snapshot.roles.size > 0 ? "authenticated" : "forbidden");
      })
      .catch(() => {
        if (mounted) {
          setStatus("unavailable");
        }
      });

    return () => {
      mounted = false;
      tokenSession.clear();
    };
  }, []);

  const login = useCallback((redirectPath?: string) => {
    void keycloakRef.current?.login({
      redirectUri: appURL(redirectPath ?? `${window.location.pathname}${window.location.search}`)
    });
  }, []);
  const logout = useCallback(() => {
    queryClient.clear();
    tokenSessionRef.current.clear();
    setRoles(new Set());
    setUserName("");
    setStatus("anonymous");
    void keycloakRef.current?.logout({
      redirectUri: appURL("/")
    });
  }, []);
  const getTokenSnapshot = useCallback(async (forceRefresh = false) => {
    const snapshot = await tokenSessionRef.current.get(forceRefresh);
    setRoles((current) => (sameRoles(current, snapshot.roles) ? current : snapshot.roles));
    return snapshot;
  }, []);
  const currentTokenSnapshot = useCallback(() => tokenSessionRef.current.current(), []);
  const onTerminalUnauthorized = useCallback((rejected: AuthTokenSnapshot) => {
    const current = tokenSessionRef.current.current();
    if (!current || current.sessionId !== rejected.sessionId || current.generation !== rejected.generation) {
      return;
    }
    queryClient.clear();
    tokenSessionRef.current.clear();
    setUserName("");
    setRoles(new Set());
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      tokenReady: status === "authenticated",
      userName,
      roles,
      login,
      logout,
      getTokenSnapshot,
      currentTokenSnapshot,
      onTerminalUnauthorized
    }),
    [currentTokenSnapshot, getTokenSnapshot, login, logout, onTerminalUnauthorized, roles, status, userName]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function keycloakTokenSource(keycloak: Keycloak): TokenSessionSource {
  return {
    updateToken: (minimumValiditySeconds) => keycloak.updateToken(minimumValiditySeconds),
    readToken: () => keycloak.token,
    readRoles: () => readSupportedRoles(keycloak)
  };
}

function readSupportedRoles(keycloak: Keycloak) {
  const parsed = keycloak.tokenParsed as
    | {
        realm_access?: { roles?: unknown };
        resource_access?: Record<string, { roles?: unknown }>;
      }
    | undefined;
  const realmRoles = Array.isArray(parsed?.realm_access?.roles) ? parsed.realm_access.roles : [];
  const resourceRoles = parsed?.resource_access?.[env.keycloakClientId]?.roles;
  return parseSupportedRoles([...realmRoles, ...(Array.isArray(resourceRoles) ? resourceRoles : [])]);
}

function parseSupportedRoles(values: unknown[]) {
  const supported = new Set<LicensingRole>();
  for (const role of licensingRoles) {
    if (values.includes(role)) {
      supported.add(role);
    }
  }
  return supported;
}

function sameRoles(left: ReadonlySet<LicensingRole>, right: ReadonlySet<LicensingRole>) {
  return left.size === right.size && [...left].every((role) => right.has(role));
}

function appURL(path: string) {
  const basePath = baseUrl === "/" ? "" : baseUrl.replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${window.location.origin}${basePath}${normalizedPath}`;
}

function readUserName(keycloak: Keycloak) {
  const parsed = keycloak.tokenParsed;
  if (!parsed) {
    return "";
  }
  const email = typeof parsed.email === "string" ? parsed.email : "";
  const preferred = typeof parsed.preferred_username === "string" ? parsed.preferred_username : "";
  return email || preferred || parsed.sub || "";
}
